import React, { useContext, useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router";
import {
  LuSparkles, LuCode, LuLightbulb,
  LuGlobe, LuSmartphone, LuPalette, LuCloud, LuShieldCheck, LuWrench,
  LuUsers, LuGitBranch, LuBriefcase, LuCalendarDays, LuFileText, LuClipboardCheck,
} from "react-icons/lu";
import { Images } from "../controller/Images";
import { GlobalStateContext } from "../controller/GlobalState";
import {
  emailRegex, isValidPhone, companyServices,
  workflowsteps, chooseus,
  company, emailaddress,
} from "../controller/global";
import { submitLead } from "../controller/leads";
import useHomeMotion from "../hooks/useHomeMotion";
import kraftigoLogo from "../assets/images/kraftigo/kraftigo-wordmark.png";
import vetingoLogo from "../assets/images/vetingo/vetingo-logo.png";
import "../styles/industry-products.css";
import cledgeLogo from "../assets/images/cledge/cledge-wordmark-white.png";

// Same trust-logo set shown across the rest of the redesigned site
// (/services, /ai-solutions, /case-studies).
const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

// Three business outcomes supported by the company’s AI and transformation services.
const AI_CAPABILITIES = [
  {
    num: "01 / MACHINE LEARNING",
    title: "Machine Learning for personalized experiences.",
    copy: "Help each customer find products that fit their interests. We build Machine Learning recommendation engines that learn from customer behavior and bring relevant suggestions into your website or app.",
    tags: ["Machine Learning", "Recommendation engines", "Personalization"],
    path: "/recommendation-engine-development",
    link: "Explore recommendation engine development",
  },
  {
    num: "02 / AI DEVELOPMENT",
    title: "AI development for smarter workflows.",
    copy: "Less searching. Less repetition. More time to focus. Our AI development services connect AI assistants to your company documents and automate defined workflow steps, with access controls and people reviewing decisions where needed.",
    tags: ["Generative AI", "AI agents", "System integration"],
    path: "/ai-solutions",
    link: "Explore AI development services",
  },
  {
    num: "03 / DIGITAL TRANSFORMATION",
    title: "Digital Transformation for connected operations.",
    copy: "Stop making people carry information between disconnected tools. We integrate business systems, modernize older applications and plan cloud migration in stages, so teams can spend less time re-entering data and chasing updates.",
    tags: ["Legacy modernization", "Cloud migration", "Process automation"],
    path: "/digital-transformation",
    link: "Explore Digital Transformation services",
  },
];

const ABOUT_POINTS = [
  {
    icon: LuGlobe,
    title: "Custom Web Development",
    desc: "Custom dashboards, internal tools and customer-facing applications designed around your workflows and integration requirements.",
  },
  {
    icon: LuSmartphone,
    title: "Mobile App Development",
    desc: "iOS and Android applications built with React Native or Flutter, with features such as notifications and offline access scoped to your needs.",
  },
  {
    icon: LuCloud,
    title: "Cloud & Infrastructure",
    desc: "Cloud infrastructure on AWS, Azure and Google Cloud, supported by deployment automation, infrastructure as code and monitoring.",
  },
  {
    icon: LuShieldCheck,
    title: "Enterprise Security",
    desc: "Security assessments, penetration testing and application safeguards, including access controls, encryption and dependency updates.",
  },
];

// A curated 6 of the 11 real company services — the full list lives on
// /services, this is just a representative entry point.
const FEATURED_SERVICE_KEYS = [
  "web_development", "mobile_app_development", "ui_ux",
  "cloud_development", "cyber_security", "product_support",
];
const SERVICE_ICONS = {
  web_development: LuGlobe,
  mobile_app_development: LuSmartphone,
  ui_ux: LuPalette,
  cloud_development: LuCloud,
  cyber_security: LuShieldCheck,
  product_support: LuWrench,
};
const HOME_SERVICE_COPY = {
  web_development: "Build customer portals, dashboards and web applications that connect people with the information and tasks they need.",
  mobile_app_development: "Bring your services to iOS and Android with mobile applications designed around your users and workflows.",
  ui_ux: "Make complex tasks easier to complete with clear interfaces, accessible design and practical user journeys.",
  cloud_development: "Support application growth with cloud infrastructure, automated deployments and monitoring.",
  cyber_security: "Identify application risks and strengthen access controls, data protection and security practices.",
  product_support: "Keep your software useful after launch with bug fixes, updates and enhancements under an agreed support scope.",
};
const FEATURED_SERVICES = FEATURED_SERVICE_KEYS
  .map((key) => companyServices.find((s) => s.key === key))
  .filter(Boolean);

const FACTS = [
  { a: "5", b: "Published case studies" },
  { a: "7", b: "Delivery stages" },
  { a: "Delhi NCR", b: "India-based team" },
  { a: "Worldwide", b: "Remote client delivery" },
];

// Same three engagement models described on /services.
const ENGAGEMENT_MODELS = [
  { a: "Fixed-Scope", b: "An agreed scope, budget and delivery plan for clearly defined requirements" },
  { a: "Dedicated Team", b: "Ongoing engineering capacity aligned with your roadmap and changing priorities" },
  { a: "MVP Sprint", b: "A focused first release to validate core features with users" },
  { a: "1", b: "A coordinated delivery process across every engagement model" },
];

// Same segments described on /services — who actually hires us, and why.
const WHO_WE_BUILD_FOR = [
  { a: "Early-stage startups", b: "Validate your product idea with an MVP focused on essential features, user feedback and a foundation for future development." },
  { a: "Growing SMBs", b: "Connect business systems, automate manual processes and develop applications that support a growing team and customer base." },
  { a: "Enterprises", b: "Modernize legacy applications, integrate complex systems and plan phased delivery with documentation for your internal teams." },
  { a: "E-commerce brands", b: "Build responsive storefronts, integrate payments and inventory, and improve the product discovery and checkout experience." },
  { a: "SaaS product teams", b: "Extend your engineering capacity with developers who collaborate within your tools, roadmap and release process." },
  { a: "Agencies & consultancies", b: "Add development capacity for client projects with technical delivery coordinated around your account management and project requirements." },
  { a: "Non-profits & public sector", b: "Develop accessible, maintainable applications with a defined scope, practical budget and documentation for handover." },
  { a: "Internal platform teams", b: "Strengthen cloud operations with deployment pipelines, infrastructure automation and monitoring for business applications." },
];

// Real pages this site already has, just not linked from Home yet.
const BEYOND_LINKS = [
  { icon: LuUsers, title: "Meet the team", desc: "Meet the people who bring engineering, design and delivery expertise to our projects.", path: "/our-core-team" },
  { icon: LuGitBranch, title: "How we work", desc: "Explore our seven-stage development process, including project activities, review points and deliverables.", path: "/how-we-work-and-function" },
  { icon: LuBriefcase, title: "We're hiring", desc: "Explore opportunities to build software and collaborate on projects across industries.", path: "/career" },
];

// Five openers that cover almost every enquiry we get. A blank "Subject"
// field asks the visitor to invent the words for something they have not
// scoped yet, which is the point most people abandon the form; picking one
// fills it for them and tells us what kind of brief it is before we reply.
const NEEDS = [
  "A new product or MVP",
  "An existing system to fix or scale",
  "AI added to something we already run",
  "Extra engineers alongside our team",
  "Not sure yet, need advice",
];

// What actually happens after the button. Every claim here is one the page
// already makes elsewhere; none of it is a new promise.
const NEXT_STEPS = [
  { n: "01", t: "Share your goals and requirements", d: "Describe your idea, current challenges and priorities. You can begin with a short overview." },
  { n: "02", t: "We review your inquiry", d: "We aim to reply within one business day with follow-up questions or a suggested time to discuss your project." },
  { n: "03", t: "Discuss the next steps", d: "We discuss your requirements and identify whether an assessment, proposal or focused first release is the right starting point." },
];

const HOME_REASONS = chooseus.map((item, index) => ({
  ...item,
  ...[
    { title: "Technology matched to your needs", description: "We select frameworks and platforms based on your requirements, existing systems and maintenance needs." },
    { title: "Coordinated project delivery", description: "Requirements, design, development and testing follow a shared plan with clear responsibilities and review points." },
    { title: "Design and engineering together", description: "Designers and developers collaborate to connect user needs with practical, usable interfaces." },
    { title: "Maintainable software", description: "We focus on code structure, performance and documentation to support future updates and handover." },
    { title: "Experienced technical specialists", description: "Our developers, engineers and analysts bring expertise across application development, cloud and AI." },
    { title: "Clear communication", description: "We collaborate with your stakeholders through agreed updates, feedback and shared project priorities." },
  ][index],
}));

const PROCESS_STEPS = workflowsteps.map((w) => ({ n: w.number, t: w.title, d: w.description }));

// The hero's signature moment: a typed build log standing in for a real
// engagement, using the same 7-stage process and service breadth described
// elsewhere on the page — not invented metrics, just that same information
// dramatized as a console session.
const TERMINAL_LINES = [
  { type: "cmd", text: "inqmise scope --client=new" },
  { type: "ok", text: "Requirements locked — stage 1 of 7" },
  { type: "cmd", text: "inqmise build --web --mobile --cloud" },
  { type: "ok", text: "Development aligned with the agreed scope" },
  { type: "cmd", text: "inqmise test --quality-checks" },
  { type: "ok", text: "QA passed — stage 6 of 7" },
  { type: "cmd", text: "inqmise deploy --ai=agentic-workflow" },
  { type: "ok", text: "Live in production" },
];

function useTypedLines(lines, reduceMotion) {
  const [rendered, setRendered] = useState(() => lines.map(() => ""));
  const [activeIndex, setActiveIndex] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (reduceMotion) {
      setRendered(lines.map((l) => l.text));
      setDone(true);
      return;
    }
    let lineIdx = 0;
    let charIdx = 0;
    let cancelled = false;
    let timer;
    const tick = () => {
      if (cancelled) return;
      if (lineIdx >= lines.length) {
        setDone(true);
        return;
      }
      // Snapshot both indices as locals so the setRendered updater below
      // stays pure — React 18 can invoke an updater function more than once
      // (e.g. Strict Mode), and it must not read the outer `let`s, which
      // may have already advanced past the end of `lines` by the replay.
      const currentLine = lineIdx;
      charIdx += 1;
      const currentChar = charIdx;
      setActiveIndex(currentLine);
      setRendered((prev) => {
        const next = [...prev];
        next[currentLine] = lines[currentLine].text.slice(0, currentChar);
        return next;
      });
      if (currentChar >= lines[currentLine].text.length) {
        lineIdx = currentLine + 1;
        charIdx = 0;
        timer = setTimeout(tick, 340);
      } else {
        timer = setTimeout(tick, 16 + Math.random() * 22);
      }
    };
    timer = setTimeout(tick, 500);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion]);

  return { rendered, activeIndex, done };
}

// Counts up from 0 to the real figure once it scrolls into view; non-numeric
// facts (e.g. "MSME") just render as-is.
function CountUpStat({ value, label, reduceMotion }) {
  const match = /^(\d+)(.*)$/.exec(value);
  const target = match ? parseInt(match[1], 10) : null;
  const suffix = match ? match[2] : "";
  const [count, setCount] = useState(target !== null && !reduceMotion ? 0 : target ?? 0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (target === null || reduceMotion) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || started.current) return;
      started.current = true;
      const duration = 900;
      const startTime = performance.now();
      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        setCount(Math.round(target * (1 - (1 - progress) ** 3)));
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
      observer.disconnect();
    }, { threshold: 0.4 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, reduceMotion]);

  return (
    <div className="hm-stat" ref={ref}>
      <strong>{target !== null ? count : value}{suffix}</strong>
      <span>{label}</span>
    </div>
  );
}

const FAQS = [
  {
    q: "How can Machine Learning help my business?",
    a: "Machine Learning can identify patterns in customer behavior and use them to suggest relevant products or content. We build recommendation engines around your use case, available data and integration needs, with evaluation before launch.",
  },
  {
    q: "What does Digital Transformation involve?",
    a: "Digital Transformation connects improvements to your processes, software and data. Depending on your needs, this can include integrating business systems, automating repetitive tasks, modernizing legacy applications and planning a phased cloud migration.",
  },
  {
    q: "What can you build for our business?",
    a: "We build AI assistants, recommendation engines, SaaS products, custom CRM and ERP systems, web and mobile applications, plus the integrations and cloud foundations that connect them to your business. We begin with the problem you need to solve, then recommend the smallest useful first step.",
  },
  {
    q: "Can you add AI to our existing software, CRM or ERP?",
    a: "Yes. We assess your workflow, data quality, user permissions and existing systems before defining a practical AI integration. This can include knowledge assistants, document processing, recommendations or defined agent workflows with appropriate review controls.",
  },
  {
    q: "What engagement models do you offer?",
    a: "We offer fixed-scope projects, dedicated teams and MVP sprints. The appropriate model depends on how clearly your requirements are defined, your development priorities and the capacity you need.",
  },
  {
    q: "Do you work with clients outside Delhi NCR?",
    a: "Yes. We are based in Delhi NCR and collaborate remotely with clients in India and internationally. Our portfolio includes Three Lock Box, a construction finance platform built for the US market.",
  },
  {
    q: "Where can I see your software development projects?",
    a: "Our case studies cover insurance quoting, construction finance, strategy execution, fitness system integration and AI-powered retail. Each project page explains the business challenge and the solution developed.",
  },
  {
    q: "How much does custom software development cost?",
    a: "Costs depend on the features, integrations, data preparation, security requirements and testing involved. We assess your requirements before preparing an estimate with the proposed scope, milestones and key risks.",
  },
  {
    q: "How long does a software development project take?",
    a: "Delivery timelines vary with project scope, technical complexity and dependencies. A focused MVP typically involves less work than a full production platform. We define a project-specific timeline after assessing your requirements.",
  },
  {
    q: "Do you provide maintenance after launch?",
    a: "Yes. We provide software maintenance and product support, including updates, bug fixes, troubleshooting and feature enhancements. Support coverage and responsibilities are defined in the agreed engagement scope.",
  },
  {
    q: "Where is InQmise Technologies based?",
    a: "InQmise Technologies is an MSME-registered IT company based in Delhi NCR, serving local and international clients through a structured seven-stage delivery process.",
  },
];

const Home = () => {
  const navigate = useNavigate();
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();
  const terminal = useTypedLines(TERMINAL_LINES, reduceMotion);
  const homeRef = useRef(null);
  useHomeMotion(homeRef, reduceMotion);

  // The title, description and canonical this effect used to write are all
  // owned by useSocialMeta (via pageDescriptions.js) and were immediately
  // overwritten by it — the competing title write was visible as a flicker on
  // load, and useSocialMeta's resolveDescription existed partly to clean up
  // the duplicate description tag this appended. The keywords meta went with
  // them: Google stopped reading it in 2009.
  //
  // The FAQ schema is this page's own and stays, but needs the cleanup the
  // old block never had. Under StrictMode the effect runs twice, and every
  // client-side return to / ran it again, so the scripts stacked up in
  // <head> — I measured two FAQPage blocks on a single load.
  useEffect(() => {
    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({ "@type": "Question", "name": item.q, "acceptedAnswer": { "@type": "Answer", "text": item.a } })),
    });
    document.head.appendChild(faqScript);
    return () => faqScript.remove();
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname": return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject": return value.trim() ? "" : "Subject is required.";
      case "description": return value.trim() ? "" : "Tell us a little about your project.";
      default: return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      return;
    }
    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => setContactStatus("error"));
  };

  const openContactModal = () => setIsOpen(true);

  return (
    <main ref={homeRef} className="editorial-ai hm-page pt-20">
      {/* HERO */}
      <header className="ea-hero hm-hero ea-wrap">
        <div className="hm-hero-content">
          <p className="ea-eyebrow">Digital Transformation & AI Development · Delhi NCR</p>
          <h1>
            <span className="hm-title-mask"><span className="hm-title-line">Driving digital transformation</span></span><em className="hm-title-mask"><span className="hm-title-line">with AI &amp; machine learning.</span></em>
          </h1>
          <p className="ea-lede">
            InQmise helps businesses modernize the way they work with AI, automation and connected digital systems. From AI assistants and workflow automation to custom CRM, ERP and SaaS platforms, we turn complex business needs into practical, dependable solutions.
          </p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start your project">
              Discuss your project ↗
            </button>
            <a
              className="ea-text-link"
              href="#process"
              onClick={(e) => { e.preventDefault(); document.getElementById("process")?.scrollIntoView({ behavior: "smooth" }); }}
            >
              See how we work
            </a>
          </div>
          <div className="hm-hero-links" aria-label="Explore our core services">
            <a href="/recommendation-engine-development" onClick={(e) => { e.preventDefault(); navigate("/recommendation-engine-development"); }}>Machine Learning</a>
            <a href="/digital-transformation" onClick={(e) => { e.preventDefault(); navigate("/digital-transformation"); }}>Digital Transformation</a>
            <a href="/ai-solutions" onClick={(e) => { e.preventDefault(); navigate("/ai-solutions"); }}>AI development</a>
          </div>
        </div>

        <div className="hm-hero-showcase">
          <div className="hm-showcase-glow hm-showcase-glow-leaf" aria-hidden="true" />
          <div className="hm-showcase-glow hm-showcase-glow-sun" aria-hidden="true" />
          <aside className="hm-terminal">
          <div className="hm-terminal-bar">
            <span className="hm-terminal-dot" /><span className="hm-terminal-dot" /><span className="hm-terminal-dot" />
            <span className="hm-terminal-live">DELIVERY PROCESS</span>
          </div>
          <div className="hm-terminal-body">
            {TERMINAL_LINES.map((line, i) => (
              <div key={line.text} className={line.type === "cmd" ? "hm-terminal-cmd" : "hm-terminal-ok"}>
                <span className="hm-terminal-measure" aria-hidden="true">{line.text}<span className="hm-cursor" /></span>
                <span className="hm-terminal-text">{terminal.rendered[i]}{!terminal.done && i === terminal.activeIndex && <span className="hm-cursor" />}</span>
              </div>
            ))}
            <div className="hm-terminal-status" style={{ visibility: terminal.done ? "visible" : "hidden" }}>
              From discovery to deployment · Delhi NCR<span className="hm-cursor" />
            </div>
          </div>
          </aside>
        </div>
      </header>

      {/* PROOF BAND — the figures and the client logos were two separate
          blocks; they make one argument, so they are one section. The logos
          used to render at grayscale(1) and opacity .5, which turned the
          strongest trust asset on the page into decoration. */}
      <section className="hm-proof" aria-label="InQmise Technologies by the numbers">
        <div className="hm-stats">
          <div className="ea-wrap hm-stats-row">
            {FACTS.map((m) => (
              <CountUpStat key={m.a} value={m.a} label={m.b} reduceMotion={reduceMotion} />
            ))}
          </div>
        </div>
        <div className="ea-wrap ea-logo-row">
          <span>BUSINESSES WE HAVE WORKED WITH</span>
          <div className="ea-logo-track">
            <div className="ea-logo-track-inner">
              {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
                <span className="hm-trust-logo-slot" key={`${logo.name}-${i}`} aria-hidden={i >= TRUST_LOGOS.length ? true : undefined}>
                  <img src={logo.src} alt={i < TRUST_LOGOS.length ? logo.name : ""} className={`hm-trust-logo hm-trust-logo-${logo.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}${i >= TRUST_LOGOS.length ? " hm-logo-duplicate" : ""}`} loading="lazy" />
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="ea-wrap hm-proof-note">
          <p>
            Explore our case studies in insurance quoting, construction
            finance, strategy management, fitness technology and AI-powered retail.
            {" "}
            <a href="/case-studies" onClick={(e) => { e.preventDefault(); navigate("/case-studies"); }}>Explore our case studies →</a>
          </p>
        </div>
      </section>

      {/* WHAT WE DO — one section, four former ones. "AI capabilities", the six
          services, the full taxonomy and the tech stack all answered the same
          question with their own full-size heading, roughly 40% of the page
          spent on breadth nobody doubted. The tech block also used to repeat
          the FACTS figures already shown in the stats band; that copy is gone. */}
      <section className="ea-wrap ea-section hm-section" id="services">
        <div className="ea-section-head hm-head reveal-wipe">
          <div>
            <div className="ea-eyebrow">What we do</div>
            <h2 className="hm-wipe-heading"><span className="hm-wipe-copy">Build the AI and software your business can put to work.</span></h2>
          </div>
          <p>Too much manual work? Customer data sitting unused? Systems that do not talk to each other? We use AI, custom software and integration to solve the operational problem behind the symptom.</p>
        </div>
        <div className="hm-subhead" id="ai">
          <h3>AI systems built around a useful job.</h3>
          <p>We start with the decision, task or customer moment that needs to improve—not a technology demo—and define the data, guardrails and outcome before building.</p>
        </div>
        <div className="hm-ai-grid">
        {AI_CAPABILITIES.map((item) => (
          <article className="ea-feature" key={item.title}>
            <div className="ea-num">{item.num}</div>
            <h3>{item.title}</h3>
            <p>{item.copy}</p>
            <div className="ea-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
            <a className="ea-text-link" href={item.path} onClick={(e) => { e.preventDefault(); navigate(item.path); }}>{item.link} →</a>
          </article>
        ))}
        </div>
        <div style={{ marginTop: 24 }}>
          <a className="ea-text-link" href="/ai-solutions" onClick={(e) => { e.preventDefault(); navigate("/ai-solutions"); }}>Explore AI development services →</a>
        </div>

        <div className="hm-subhead">
          <h3>Software that connects the work around it.</h3>
          <p>From SaaS products and customer portals to CRM, ERP and cloud infrastructure, we build the systems that let your teams and AI solutions work together.</p>
        </div>
        <div className="ea-cards">
          {FEATURED_SERVICES.map((service) => {
            const Icon = SERVICE_ICONS[service.key];
            return (
              <a
                key={service.key}
                className="ea-card hm-service-card"
                href={service.path}
                onClick={(e) => { e.preventDefault(); navigate(service.path); }}
                style={{ textDecoration: "none" }}
              >
                <div className="ea-icon"><Icon aria-hidden="true" /></div>
                <div>
                  <h3>{service.title}</h3>
                  <p>{HOME_SERVICE_COPY[service.key]}</p>
                </div>
              </a>
            );
          })}
        </div>
        <div style={{ marginTop: 24 }}>
          <a className="ea-text-link" href="/services" onClick={(e) => { e.preventDefault(); navigate("/services"); }}>View all 11 services →</a>
        </div>

        <section className="hm-specialists" aria-labelledby="specialist-heading">
          <div className="hm-specialists-intro">
            <span className="hm-specialists-label"><LuSparkles aria-hidden="true" /> Specialist expertise</span>
            <h3 id="specialist-heading">Make your next move intelligent.</h3>
            <p>Put your data to work. Give your people better tools. Build a business that’s ready for what comes next.</p>
            <button className="hm-specialists-cta" onClick={openContactModal}>Find the right solution <span aria-hidden="true">↗</span></button>
            <div className="hm-specialists-footnote">From your first use case to integration and ongoing support.</div>
          </div>
          <div className="hm-specialists-paths">
            {[
              { icon: LuSparkles, title: "Machine Learning", outcome: "Turn customer data into discovery.", copy: "Help customers find what fits with recommendation engines and personalized product experiences.", links: [{ title: "Recommendation engines", path: "/recommendation-engine-development" }] },
              { icon: LuUsers, title: "AI Development", outcome: "Give your team more room to think.", copy: "Connect AI assistants to your knowledge and automate repetitive steps within your existing workflows.", links: [{ title: "Generative AI", path: "/generative-ai-development" }, { title: "AI agents", path: "/agentic-ai-development" }, { title: "LLM integration", path: "/llm-integration-services" }] },
              { icon: LuGitBranch, title: "Digital Transformation", outcome: "Connect systems. Move work forward.", copy: "Modernize legacy applications, integrate business tools and plan your move to the cloud around real operational needs.", links: [{ title: "Legacy modernization", path: "/legacy-system-modernization" }, { title: "Transformation roadmap", path: "/digital-transformation-roadmap" }] },
            ].map((service) => (
              <article className="hm-specialist-path" key={service.title}>
                <div className="hm-specialist-icon"><service.icon aria-hidden="true" /></div>
                <div className="hm-specialist-body">
                  <h4>{service.title}</h4>
                  <p className="hm-specialist-outcome">{service.outcome}</p>
                  <p className="hm-specialist-copy">{service.copy}</p>
                  <div className="hm-specialist-links">
                    {service.links.map((link) => <a key={link.path} href={link.path} onClick={(e) => { e.preventDefault(); navigate(link.path); }}>{link.title}<span aria-hidden="true">↗</span></a>)}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="hm-fit-work" id="tech" aria-labelledby="home-fit-work-heading">
          <div className="hm-fit-work-intro">
            <h3 id="home-fit-work-heading">Built to fit the way you work.</h3>
            <p>A useful solution belongs in your everyday operations. We plan around the tools you use, the people you support and what comes after launch.</p>
            <a className="ea-text-link" href="/case-studies" onClick={(e) => { e.preventDefault(); navigate("/case-studies"); }}>See our work in practice →</a>
          </div>
          <div className="hm-fit-work-points">
            <div><LuGitBranch aria-hidden="true" /><div><h4>Connect what you already use</h4><p>Bring your applications and data together through integrations scoped to your existing systems.</p></div></div>
            <div><LuUsers aria-hidden="true" /><div><h4>Make everyday tasks easier</h4><p>Design clear interfaces and workflows around the people who will use the solution.</p></div></div>
            <div><LuWrench aria-hidden="true" /><div><h4>Keep improving after launch</h4><p>Plan for handover, maintenance and updates as your business needs evolve.</p></div></div>
          </div>
        </section>
      </section>

      {/* HOW IT RUNS — the seven stages and the three commercial models were
          two sections answering one question: how will this actually go. */}
      <section className="ea-band" id="process">
        <div className="ea-wrap ea-section hm-section">
          <div className="ea-section-head hm-head reveal-wipe">
            <div>
              <div className="ea-eyebrow">How we work</div>
            <h2>Start with an idea. Finish with something useful.</h2>
            </div>
            <p>First, we agree on what needs to improve and how to measure it. Then we plan, build and test with your team, using clear milestones to review progress before the next stage.</p>
          </div>
          <ol className="hm-process-grid">
            {PROCESS_STEPS.map((step) => (
              <li className="hm-process-step" key={step.n}>
                <span className="hm-process-number">{step.n}</span>
                <div><h3>{step.t}</h3><p>{step.d}</p></div>
              </li>
            ))}
          </ol>

          <div className="hm-subhead">
            <h3>Three ways to work with us.</h3>
            <p>One accountable team, whichever model fits your project.</p>
          </div>
          <div className="ea-case">
            <div>
              <div className="ea-eyebrow">FIXED-SCOPE / DEDICATED / MVP</div>
              <h3>The right engagement for your next step.</h3>
              <p>Validate an idea with an MVP, deliver defined requirements through a fixed-scope project, or add a dedicated team to your roadmap. Choose the model around your priorities, budget and internal capacity.</p>
              <button className="ea-text-link" onClick={openContactModal}>Discuss your project</button>
            </div>
            <div className="ea-results">
              {ENGAGEMENT_MODELS.map((r) => (
                <div key={r.a}><b>{r.a}</b><span>{r.b}</span></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OUR PRODUCT */}
      <section className="ea-band hm-product-section" id="product" aria-labelledby="home-product-heading">
        <div className="ea-wrap ea-section hm-section">
          <div className="hm-product-section-head">
            <h2 id="home-product-heading">Our Products</h2>
            <p>Purpose-built software for the operational work teams need to make clearer, faster and easier to manage.</p>
          </div>
          <div className="hm-cledge-showcase">
            <div className="hm-cledge-intro">
              <img src={cledgeLogo} alt="Cledge" width="351" height="132" loading="lazy" decoding="async" />
              <h3>Manage your clinic’s daily work with Cledge.</h3>
              <p>Appointments, patient records, prescriptions and follow-ups in one place. Built for Indian clinics, from a single practice to multiple branches.</p>
              <div className="hm-cledge-audience"><LuUsers aria-hidden="true" /><span>For clinic owners, doctors and front desk teams</span></div>
              <div className="hm-cledge-actions">
                <a className="hm-cledge-primary" href="/products/cledge" onClick={(e) => { e.preventDefault(); navigate("/products/cledge"); }}>Discover Cledge <span aria-hidden="true">↗</span></a>
                <a href="https://cledge.in/" target="_blank" rel="noopener noreferrer">Visit cledge.in <span aria-hidden="true">↗</span></a>
              </div>
            </div>
            <div className="hm-cledge-features">
              <div className="hm-cledge-features-heading"><span>One platform. Every part of the visit.</span><LuClipboardCheck aria-hidden="true" /></div>
              <article><LuCalendarDays aria-hidden="true" /><div><h4>Appointments &amp; check-in</h4><p>Organize bookings and keep your front desk queue moving.</p></div></article>
              <article><LuFileText aria-hidden="true" /><div><h4>Patient records &amp; prescriptions</h4><p>Keep patient history and consultation details together.</p></div></article>
              <article><LuGitBranch aria-hidden="true" /><div><h4>Follow-ups &amp; multiple branches</h4><p>Track treatment packages and follow-ups across your clinics.</p></div></article>
              <a className="hm-cledge-details" href="/products/cledge" onClick={(e) => { e.preventDefault(); navigate("/products/cledge"); }}>Explore product features <span aria-hidden="true">→</span></a>
            </div>
          </div>
          <div className="hm-more-products">
            <article className="hm-product-spotlight hm-boxgenie-spotlight">
              <div className="hm-product-spotlight-top"><span>KRAFTIGO</span><span>MANUFACTURING OPERATIONS</span></div>
              <img src={kraftigoLogo} alt="Kraftigo" width="210" height="59" loading="lazy" />
              <h3>One operating picture, from proposal to production.</h3>
              <p>Kraftigo connects customer records, box specifications, quotations, sales orders, production job cards and inventory in one workspace for corrugated packaging manufacturers.</p>
              <ul><li>Keep specifications and bill-of-material details close to every order</li><li>Plan production with job cards, routing and material availability in view</li><li>Give sales, production and inventory teams the same current information</li></ul>
              <a href="/products/kraftigo" onClick={(e) => { e.preventDefault(); navigate("/products/kraftigo"); }}>Explore Kraftigo <span aria-hidden="true">↗</span></a>
            </article>
            <article className="hm-product-spotlight hm-vetingo-spotlight">
              <div className="hm-product-spotlight-top"><span>VETINGO</span><span>CONNECTED TALENT</span></div>
              <img className="hm-vetingo-brand" src={vetingoLogo} alt="Vettingo" loading="lazy" />
              <h3>Give every hiring conversation better context.</h3>
              <p>Vettingo turns a role brief into a structured hiring flow—from job descriptions and resume parsing to explainable candidate scoring and tailored assessments.</p>
              <ul><li>Create consistent role requirements before candidate review begins</li><li>Turn unstructured resumes into readable, comparable candidate records</li><li>Keep recruiter judgement in control with transparent scores and reasoning</li></ul>
              <a href="/products/vettingo" onClick={(e) => { e.preventDefault(); navigate("/products/vettingo"); }}>Explore Vettingo <span aria-hidden="true">↗</span></a>
            </article>
          </div>
        </div>
      </section>

      <section className="ea-wrap ea-section hm-section" aria-labelledby="home-opportunity-heading">
        <div className="hm-launch">
          <div className="hm-launch-heading">
            <span className="hm-launch-label">Built around your business</span>
            <h2 id="home-opportunity-heading">Your next idea.<br />Built for the real world.</h2>
            <p>Bring us a business challenge or a product idea. We bring the engineering to turn it into software your team can use.</p>
            <button className="ea-button ea-primary" onClick={openContactModal}>Talk through your idea ↗</button>
          </div>
          <div className="hm-launch-workspace">
            <div className="hm-launch-workspace-head"><LuCode aria-hidden="true" /><span>From possibility to something practical</span></div>
            <div className="hm-launch-deliverables">
              <article><LuFileText aria-hidden="true" /><div><h3>A clear direction</h3><p>A defined use case, the systems it touches and what success should look like.</p></div></article>
              <article><LuSmartphone aria-hidden="true" /><div><h3>A working first version</h3><p>A focused prototype or MVP to test with your users before expanding the scope.</p></div></article>
              <article><LuGitBranch aria-hidden="true" /><div><h3>A connected solution</h3><p>Integration with your existing tools, with testing, handover and support planned around your needs.</p></div></article>
            </div>
            <div className="hm-launch-bottom"><span>Machine Learning · AI Development · Digital Transformation</span><a href="/how-we-work-and-function" onClick={(e) => { e.preventDefault(); navigate("/how-we-work-and-function"); }}>See our delivery approach <span aria-hidden="true">↗</span></a></div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR — the stage you're at and where you are were two thin
          sections asking the same thing: is this for someone like me. */}
      <section className="ea-wrap ea-section hm-section">
        <div className="hm-head-inline reveal-wipe">
          <div className="ea-eyebrow">Who we build for</div>
          <h2>Support for your next stage of growth.</h2>
          <p>We adapt the project scope and delivery model to your organization, priorities and existing technology.</p>
        </div>
        <div className="ea-industries">
          {WHO_WE_BUILD_FOR.map((item) => (
            <div className="ea-industry" key={item.a}>
              <b>{item.a}</b>
              <p>{item.b}</p>
            </div>
          ))}
        </div>

        <div className="hm-fit" aria-labelledby="home-fit-heading">
          <div className="hm-fit-intro"><LuLightbulb aria-hidden="true" /><h3 id="home-fit-heading">The right technology starts with the right problem.</h3><p>AI is one option. We help you choose an approach that fits the work, your data and the people using it.</p></div>
          <div className="hm-fit-options">
            <div><h4>Understand patterns</h4><p>Machine Learning for recommendations and personalized discovery.</p></div>
            <div><h4>Work with knowledge</h4><p>AI assistants for finding information and supporting defined tasks.</p></div>
            <div><h4>Connect everyday work</h4><p>Software integration and automation for clear, repeatable processes.</p></div>
          </div>
          <button className="ea-text-link" onClick={openContactModal}>Discuss what fits your business ↗</button>
        </div>
      </section>

      {/* WHY US — "Less vendor, more partner", the six reasons, and the links
          out were three sections all answering: why not the cheaper quote. */}
      <section className="ea-band" id="about">
        <div className="ea-wrap ea-section hm-section">
          <div className="ea-section-head hm-head reveal-wipe">
            <div>
              <div className="ea-eyebrow">Why choose us</div>
              <h2>Built for your business. Ready for your team.</h2>
            </div>
            <p>Useful software earns its place in everyday work. We bring your business and technical teams into the process so the result fits your workflows, your users and the people who will maintain it.</p>
          </div>
          <div className="hm-about-overview">
            <div>
              <h3 className="hm-about-title">
                Business understanding. Technical expertise.
              </h3>
              <p className="hm-about-description">
                We begin by understanding your users, workflows and business objectives. Our engineers and designers translate those requirements into custom applications, system integrations and AI solutions, with attention to usability, security and maintainability.
              </p>
              <a className="ea-text-link" href="/about" onClick={(e) => { e.preventDefault(); navigate("/about"); }}>Discover our expertise →</a>
            </div>
            <div className="ea-cards hm-about-cards">
              {ABOUT_POINTS.map((item) => (
                <div className="ea-card" key={item.title}>
                  <div className="ea-icon"><item.icon aria-hidden="true" /></div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hm-subhead">
            <h3>What you can expect from our team.</h3>
            <p>Technical expertise, coordinated delivery and open communication throughout your project.</p>
          </div>
          <div className="ea-cards">
            {HOME_REASONS.map((item) => (
              <article className="ea-card" key={item.title}>
                <div className="ea-icon"><item.icon aria-hidden="true" /></div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="hm-subhead">
            <h3>Get to know InQmise Technologies.</h3>
            <p>Meet our team, explore our delivery approach and find career opportunities.</p>
          </div>
          <div className="ea-cards">
            {BEYOND_LINKS.map((item) => (
              <a
                key={item.path}
                className="ea-card"
                href={item.path}
                onClick={(e) => { e.preventDefault(); navigate(item.path); }}
                style={{ textDecoration: "none" }}
              >
                <div className="ea-icon"><item.icon aria-hidden="true" /></div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section hm-section">
          <div className="ea-section-head hm-head reveal-wipe">
            <div className="ea-eyebrow">Common questions</div>
            <h2>Your questions. A clearer path forward.</h2>
            <p>Short answers to the questions we hear most.</p>
          </div>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `home-faq-panel-${i}`;
            const buttonId = `home-faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button id={buttonId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenFaq(isOpen ? -1 : i)}>
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="ea-cta" aria-labelledby="home-contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <div className="reveal-wipe">
            <div className="ea-eyebrow">Discuss your project</div>
            <h2 id="home-contact-heading">Ready to turn innovation into action?</h2>
            <p>Want to personalize customer experiences, build an AI assistant or modernize your business systems? Share your goals, available data and current challenges. We’ll help you identify a practical first step.</p>

            {/* The form used to be the only way in, and it asked for a
                "Subject" before saying what happens after you send it. These
                three answer that, and the two links below give the people who
                will not fill in a form something to do instead. */}
            <ol className="hm-next">
              {NEXT_STEPS.map((s) => (
                <li key={s.n}>
                  <span className="hm-next-n">{s.n}</span>
                  <div>
                    <b>{s.t}</b>
                    <p>{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>

            {/* Visible NAP: Google cross-checks the contact details it can read
                on the page against the structured data and the Business
                Profile, and a buyer who wants to phone should not have to
                scroll to the footer to find the number. */}
            <div className="hm-direct">
              <a href={`tel:${company.phoneHref}`}>
                <span>Call</span>
                <b>{company.phone}</b>
              </a>
              <a href={`mailto:${emailaddress}`}>
                <span>Email</span>
                <b>{emailaddress}</b>
              </a>
            </div>
            <p>Already working with us? Contact your project team to plan your next improvement.</p>
            <p className="hm-direct-note">{company.street}, {company.locality}, {company.region} {company.postalCode}</p>
          </div>

          <div className="ea-contact-card">
            <span>LET’S BUILD YOUR NEXT SOLUTION</span>
            <h3>Let’s solve it together.</h3>
            <p>Share your requirements. Our team aims to respond within one business day.</p>

            <fieldset className="hm-needs">
              <legend>How can we help? Select a project type.</legend>
              <div className="hm-needs-row">
                {NEEDS.map((need) => (
                  <button
                    type="button"
                    key={need}
                    className={`hm-need${formData.subject === need ? " is-picked" : ""}`}
                    aria-pressed={formData.subject === need}
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, subject: need }));
                      setContactErrors((prev) => ({ ...prev, subject: "" }));
                    }}
                  >
                    {need}
                  </button>
                ))}
              </div>
            </fieldset>

            <form onSubmit={handleContactSubmit} aria-label="Home page project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="home-fullname">Full Name</label>
                <input
                  id="home-fullname" name="fullname" type="text" value={formData.fullname} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)} aria-describedby={contactErrors.fullname ? "home-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="home-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="home-phone">Phone Number (optional)</label>
                  <input
                    id="home-phone" name="phone" type="tel" value={formData.phone} onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)} aria-describedby={contactErrors.phone ? "home-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="home-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="home-email">Email</label>
                  <input
                    id="home-email" name="email" type="email" value={formData.email} onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)} aria-describedby={contactErrors.email ? "home-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="home-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="home-subject">Subject</label>
                <input
                  id="home-subject" name="subject" type="text" placeholder="Pick one above, or write your own"
                  value={formData.subject} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)} aria-describedby={contactErrors.subject ? "home-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="home-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="home-description">Tell us about your project</label>
                <textarea
                  id="home-description" name="description" rows={3} value={formData.description} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)} aria-describedby={contactErrors.description ? "home-description-error" : undefined}
                />
                {contactErrors.description && <p id="home-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Sending…" : "Discuss your project ↗"}
              </button>
              <p className="hm-form-note">Your inquiry goes to our team for review. We aim to respond within one business day.</p>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
